import { Badge } from '@eventbrite/eds-badge';
import { SIZE_BLOCK, STYLE_NEUTRAL } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import {
    ChevronDownChunky,
    ChevronUpChunky,
    FilterChunky as FilterChunkySvg,
} from '@eventbrite/eds-iconography';
import { Tag } from '@eventbrite/eds-tag';
import { gettext } from '@eventbrite/i18n';
import { getOnlineEventsOption } from '@eventbrite/location-autocomplete';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import compact from 'lodash/compact';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import React, { Component } from 'react';
import { useIntersection } from 'react-use';
import { TAG_BACKGROUND_COLOR, TAG_STYLE } from '../../../../constants';
import {
    CATEGORY_FILTER,
    CLEAR_PRICE_FILTER,
    CURRENCY_FILTER,
    DATE_FILTER,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    FORMAT_FILTER,
    LANGUAGE_FILTER,
    PRICE_FILTER,
} from '../../../../constants/filters';
import { BotFilters } from '../BotFilters';
import './mobileFilterHeader.scss';

const OnlineFilterTag = ({
    online,
    onClose,
    onClick,
}: {
    online?: boolean;
    onClose?: Function;
    onClick?: Function;
}) => {
    return (
        <span
            className={`facet facet--online eds-l-pad-left-1`}
            key={'online'}
            data-testid="facet"
        >
            <Tag
                initialIsActive={false}
                key={'online'}
                isClosable={online}
                isStatic={online}
                backgroundColor={online ? TAG_BACKGROUND_COLOR : undefined}
                onClose={onClose}
                onActiveToggle={() =>
                    onClick?.({ ...getOnlineEventsOption(), placeId: null })
                }
                style={TAG_STYLE}
            >
                <span className="tag-content">
                    <span className="eds-l-pad-right-1">
                        {gettext('Online')}
                    </span>
                </span>
            </Tag>
        </span>
    );
};

interface getRenderedFiltersProps {
    date?: string;
    format?: string;
    price?: string;
    category?: string;
    language?: string;
    currency?: string;
    onClose?: Function;
    onActiveToggle?: Function;
    filterTypeToShow?: $FixMe;
    online?: boolean;
    handleApplyOnlineFilter?: Function;
    handleRemoveOnlineFilter?: Function;
}

const getRenderedFilters = ({
    date,
    format,
    price,
    category,
    language,
    currency,
    onClose,
    onActiveToggle,
    filterTypeToShow,
    online,
    handleApplyOnlineFilter,
    handleRemoveOnlineFilter,
}: getRenderedFiltersProps) => {
    const filterObject: { [key: string]: any } = {
        online: online || '',
        [DATE_FILTER]: date || '',
        [CATEGORY_FILTER]: category || '',
        [FORMAT_FILTER]: format || '',
        [PRICE_FILTER]: price || CLEAR_PRICE_FILTER,
        [LANGUAGE_FILTER]: language || '',
        [CURRENCY_FILTER]: currency || '',
    };

    const filtersToShow = Object.keys(filterObject).sort((a, b) => {
        const valA =
            !filterObject[a] || filterObject[a] === CLEAR_PRICE_FILTER ? 1 : 0;
        const valB =
            !filterObject[b] || filterObject[b] === CLEAR_PRICE_FILTER ? 1 : 0;

        return valA - valB;
    });

    return filtersToShow.map((key) => {
        // Given an applied filters value, grab the appropriate
        // display value associated to that filter
        if (key === 'online') {
            return (
                <OnlineFilterTag
                    key="online"
                    online={online}
                    onClick={handleApplyOnlineFilter}
                    onClose={handleRemoveOnlineFilter}
                />
            );
        }

        const dropdownVals =
            FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].values;
        const label = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].label;

        const foundFilter = find(
            dropdownVals,
            ({ value }) =>
                value === filterObject[key] ||
                (Array.isArray(filterObject[key]) &&
                    filterObject[key][0] === value),
        );

        const filterIsShowing = filterTypeToShow === key;
        const iconType = filterIsShowing ? (
            <ChevronUpChunky />
        ) : (
            <ChevronDownChunky />
        );

        let display;

        if (Array.isArray(filterObject[key])) {
            display = `${foundFilter.display}${
                filterObject[key].length > 1
                    ? ` + ${filterObject[key].length - 1}`
                    : ''
            }`;
        } else if (foundFilter) {
            display = foundFilter.display;
        } else {
            // This is for handling pick a date, since it is variable
            // it doesn't show up in the FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP array
            display = date;
        }

        let isClosable = true;

        if (!filterObject[key]) {
            isClosable = false;
        }

        if (key === PRICE_FILTER && !price) {
            isClosable = false;
        }

        return (
            <span
                className={`facet facet--${key} eds-l-pad-left-1`}
                key={key}
                data-testid="facet"
            >
                <Tag
                    initialIsActive={false}
                    key={key}
                    isClosable={isClosable}
                    isStatic={isClosable}
                    backgroundColor={
                        isClosable ? TAG_BACKGROUND_COLOR : undefined
                    }
                    onClose={onClose?.bind(null, key)}
                    onActiveToggle={() => onActiveToggle?.(key)}
                    style={TAG_STYLE}
                >
                    <span className="tag-content">
                        <span className="eds-l-pad-right-1">
                            {isClosable ? display : label}
                        </span>
                        {!isClosable && (
                            <Icon
                                type={iconType}
                                color="grey-700"
                                size="small"
                            />
                        )}
                    </span>
                </Tag>
            </span>
        );
    });
};

const MoreFiltersToggle = ({
    onClick,
    spacingClass,
    filters,
}: {
    onClick?: Function;
    spacingClass?: string;
    filters: { [key: string]: number | string | boolean | undefined };
}) => {
    const btnProps = {
        onClick,
        style: STYLE_NEUTRAL,
        size: SIZE_BLOCK,
    };

    const numberActiveFilters = uniq(compact(values(filters))).length;
    const isFilterActive = !!numberActiveFilters;

    return (
        <span
            className={`${spacingClass} more-filters`}
            data-testid="more-filters-facet"
        >
            <Tag
                initialIsActive={false}
                onActiveToggle={btnProps.onClick}
                backgroundColor={
                    isFilterActive ? TAG_BACKGROUND_COLOR : undefined
                }
                style={TAG_STYLE}
            >
                <span className="tag-content eds-text-weight--heavy">
                    <Badge
                        count={numberActiveFilters}
                        bgColor="white"
                        contentColor="ui-blue"
                    >
                        <Icon
                            type={<FilterChunkySvg />}
                            color={isFilterActive ? 'white' : 'grey-700'}
                            size="small"
                            title="Filter"
                        />
                    </Badge>
                </span>
            </Tag>
        </span>
    );
};

interface MobileFilterHeaderProps {
    trackGAEvent?: Function;
    handleApplyFilter?: Function;
    handleRemoveFilter?: Function;
    selectedCategory?: string;
    selectedPrice?: string;
    selectedDate?: string;
    selectedFormat?: string;
    isBotRequest?: boolean;
    locationSlug?: string;
    facets?: {
        key?: string;
        count?: number;
        displayName?: string;
    }[];
    isLoading?: boolean;
    showFocusDrawer?: Function;
    closeFocusDrawer?: Function;
    addFocusDrawerNotification?: Function;
    hideFocusDrawerNotification?: Function;
    /**
     * Callback to execute when a filter is selected
     */
    openCloseAnimatedDrawer?: Function;
    /**
     * Type of individual filter active in the filter list
     */
    filterTypeToShow?: $FixMe;
    selectedLanguage?: string;
    selectedCurrency?: string;
    followedOrganizers?: boolean;
    selectedOrganizations?: boolean;
    selectedHash?: string;
    isOnline?: boolean;
    handleApplyOnlineFilter?: Function;
    handleRemoveOnlineFilter?: Function;
}

class MobileFilterHeader extends Component<MobileFilterHeaderProps> {
    _handleRemoveFilter(filterType: string) {
        const { handleRemoveFilter } = this.props;

        handleRemoveFilter?.({ [filterType]: true });
    }

    render() {
        const {
            selectedDate,
            selectedCategory,
            selectedPrice,
            selectedFormat,
            openCloseAnimatedDrawer,
            filterTypeToShow,
            selectedLanguage,
            selectedCurrency,
            followedOrganizers,
            selectedOrganizations,
            selectedHash,
            isOnline,
            handleApplyOnlineFilter,
            handleRemoveOnlineFilter,
        } = this.props;

        const handleOpenFilterPanel = (filterType: string) =>
            openCloseAnimatedDrawer?.(true, filterType);

        const appliedFiltersAsFacets = getRenderedFilters({
            date: selectedDate,
            format: selectedFormat,
            price: selectedPrice,
            category: selectedCategory,
            language: selectedLanguage,
            currency: selectedCurrency,
            onClose: this._handleRemoveFilter.bind(this),
            onActiveToggle: handleOpenFilterPanel,
            filterTypeToShow: filterTypeToShow,
            online: isOnline,
            handleApplyOnlineFilter,
            handleRemoveOnlineFilter,
        });

        const filters = {
            selectedCategory,
            selectedDate,
            selectedFormat,
            selectedPrice,
            selectedCurrency,
            selectedLanguage,
            followedOrganizers,
            selectedOrganizations,
            selectedHash,
            isOnline,
        };

        return (
            <>
                <MoreFiltersToggle
                    onClick={() => openCloseAnimatedDrawer?.(true)}
                    spacingClass="eds-show-down-sw"
                    filters={filters}
                />
                {[...appliedFiltersAsFacets]}
            </>
        );
    }
}

const MobileFilterHeaderContainer = (props: {
    isBotRequest?: boolean;
    locationSlug?: string;
}) => {
    const { isBotRequest, locationSlug } = props;
    const classes = classNames('mobile-filter-header');
    const scrollablePaneClasses = classNames(
        'mobile-filter-header__facets',
        // Give scrollbar some spaces
        'eds-l-pad-bot-4',
    );

    return (
        <section className={classes} data-spec="mobile-filter-header">
            <div className={scrollablePaneClasses}>
                {isBotRequest ? (
                    <BotFilters slug={locationSlug || ''} />
                ) : (
                    <MobileFilterHeader {...props} />
                )}
            </div>
        </section>
    );
};

const useStickyHeader = (
    ref: React.LegacyRef<HTMLElement>,
    enabled: boolean,
) => {
    const [isSticky, setIsSticky] = React.useState(false);
    const intersectionRef = enabled ? ref : { current: null };

    const intersection = useIntersection(intersectionRef, {
        root: null,
        rootMargin: '-75px 0px 0px 0px',
        threshold: 0.01,
    });

    if (intersection && intersection.intersectionRatio === 0 && !isSticky) {
        setIsSticky(true);
    }

    if (intersection && intersection.intersectionRatio > 0 && isSticky) {
        setIsSticky(false);
    }

    return isSticky;
};

const MobileFilterHeaderWithScrollWatcher = (
    props: MobileFilterHeaderProps,
) => {
    const [isMobile, setIsMobile] = React.useState(false);
    const headerRef = React.useRef(null);
    const isSticky = useStickyHeader(headerRef, isMobile);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 822px)');
        const isMobile = !mediaQuery.matches;

        if (isMobile) setIsMobile(true);
    }, []);

    const enableStickyHeader = isMobile && isSticky;

    return !props.isBotRequest ? (
        <>
            {enableStickyHeader && (
                <div style={{ height: '64px', width: '100%' }} />
            )}
            <div
                className={`${
                    enableStickyHeader ? 'mobile-filter-header--sticky' : ''
                }`}
            >
                <MobileFilterHeaderContainer {...props} />
            </div>
            <div ref={headerRef} />
        </>
    ) : (
        <MobileFilterHeaderContainer {...props} />
    );
};

export default MobileFilterHeaderWithScrollWatcher;
